import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAppNavAccess } from '../../hooks/useAppNavAccess';
import { useVisibleTools } from '../../tools/useVisibleTools';
import { filterTools } from '../../tools/appTools';
import { useStore } from '../../contexts/StoreContext';
import Icon from '../Icon/Icon';
import centreService from '../../services/centre.service';
import './BottomNavigationBar.scss';

const BottomNavigationBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ready, canCentre, canCommissions, canAdmin } = useAppNavAccess();
  const { canTools, tools } = useVisibleTools();
  const { currentStore } = useStore();
  const [showMenu, setShowMenu] = useState(false);
  const [toolQuery, setToolQuery] = useState('');
  const [centreUnread, setCentreUnread] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isActive = (path: string) => location.pathname.startsWith(path);
  const isClientsPageActive = () => location.pathname === '/clients';
  const isPlusMenuActive = () => {
    if (location.pathname.startsWith('/admin')) return true;
    if (location.pathname.startsWith('/outils')) return true;
    return canCentre && location.pathname === '/clients';
  };

  const toolResults = filterTools(tools, toolQuery);
  const toolSearch = toolQuery.trim().length > 0;
  const hasMoreLinks = canCentre || canAdmin || canCommissions;

  const handleMenuClick = (path: string) => {
    setShowMenu(false);
    setToolQuery('');
    navigate(path);
  };

  useEffect(() => {
    if (!showMenu) setToolQuery('');
  }, [showMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMenu &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (!currentStore?.id || !canCentre) {
      setCentreUnread(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      centreService.getUnread(currentStore.id).then((count) => {
        if (!cancelled) setCentreUnread(count);
      }).catch(() => {});
    };
    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [currentStore?.id, canCentre]);

  return (
    <>
      <nav className="bottom-navigation-bar">
        <Link
          to="/dashboard"
          className={`bottom-navigation-bar__item ${isActive('/dashboard') ? 'bottom-navigation-bar__item--active' : ''}`}
        >
          <Icon name="grid" size={18} />
          <span className="bottom-navigation-bar__label">Dashboard</span>
        </Link>

        <Link
          to="/orders"
          className={`bottom-navigation-bar__item ${isActive('/orders') && !isClientsPageActive() ? 'bottom-navigation-bar__item--active' : ''}`}
        >
          <Icon name="shopping-cart" size={18} />
          <span className="bottom-navigation-bar__label">Commandes</span>
        </Link>

        <button
          className="bottom-navigation-bar__item bottom-navigation-bar__item--center"
          onClick={() => navigate('/orders?create=true')}
          aria-label="Créer une commande"
        >
          <Icon name="plus" size={20} />
        </button>

        {!ready ? (
          <span className="bottom-navigation-bar__item bottom-navigation-bar__item--slot" aria-hidden />
        ) : canCentre ? (
          <Link
            to="/centre"
            className={`bottom-navigation-bar__item ${isActive('/centre') ? 'bottom-navigation-bar__item--active' : ''}`}
          >
            <span className="bottom-navigation-bar__icon-wrap">
              <Icon name="whatsapp" size={18} />
              {centreUnread > 0 && (
                <span className="bottom-navigation-bar__unread">{centreUnread > 9 ? '9+' : centreUnread}</span>
              )}
            </span>
            <span className="bottom-navigation-bar__label">Centre</span>
          </Link>
        ) : (
          <Link
            to="/clients"
            className={`bottom-navigation-bar__item ${isClientsPageActive() ? 'bottom-navigation-bar__item--active' : ''}`}
          >
            <Icon name="user" size={18} />
            <span className="bottom-navigation-bar__label">Clients</span>
          </Link>
        )}

        <button
          ref={buttonRef}
          className={`bottom-navigation-bar__item ${isPlusMenuActive() ? 'bottom-navigation-bar__item--active' : ''}`}
          onClick={() => setShowMenu(!showMenu)}
          aria-label="Menu"
          aria-expanded={showMenu}
        >
          <Icon name="more-horizontal" size={18} />
          <span className="bottom-navigation-bar__label">Plus</span>
        </button>
      </nav>

      {showMenu && createPortal(
        <div ref={menuRef} className={`bottom-navigation-bar__menu${canTools ? ' bottom-navigation-bar__menu--tools' : ''}`}>
          {canTools && (
            <>
              <label className="bottom-navigation-bar__tool-search">
                <Icon name="search" size={14} />
                <input
                  type="search"
                  value={toolQuery}
                  placeholder="Chercher un outil"
                  aria-label="Chercher un outil"
                  onChange={(event) => setToolQuery(event.target.value)}
                />
              </label>
              {toolResults.length === 0 ? (
                <p className="bottom-navigation-bar__tool-empty">Aucun outil</p>
              ) : (
                toolResults.map((tool) => (
                  <button
                    key={tool.id}
                    type="button"
                    className="bottom-navigation-bar__menu-item"
                    onClick={() => handleMenuClick(tool.path)}
                  >
                    <Icon name={tool.icon} size={16} />
                    <span>{tool.label}</span>
                  </button>
                ))
              )}
              {!toolSearch && hasMoreLinks && <div className="bottom-navigation-bar__split" />}
            </>
          )}
          {!toolSearch && canCentre && (
            <button
              className="bottom-navigation-bar__menu-item"
              onClick={() => handleMenuClick('/clients')}
            >
              <Icon name="user" size={16} />
              <span>Clients</span>
            </button>
          )}
          {!toolSearch && canAdmin && (
            <>
              <button
                className="bottom-navigation-bar__menu-item"
                onClick={() => handleMenuClick('/admin')}
              >
                <Icon name="settings" size={16} />
                <span>Admin</span>
              </button>
              <button
                className="bottom-navigation-bar__menu-item"
                onClick={() => handleMenuClick('/admin?section=stock')}
              >
                <Icon name="package" size={16} />
                <span>Stock</span>
              </button>
            </>
          )}
          {!toolSearch && canCommissions && (
            <button
              className="bottom-navigation-bar__menu-item"
              onClick={() => handleMenuClick('/my-commissions')}
            >
              <Icon name="dollar-sign" size={16} />
              <span>Commissions</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default BottomNavigationBar;
